import { useState, useEffect } from 'react';
import AppLayout from '../components/AppLayout';
import { Building2, DollarSign, AlertTriangle } from 'lucide-react';
import type { Invoice } from '../../shared/types';
import { apiFetch } from '../lib/api';

interface VendorData {
  name: string;
  totalSpend: number;
  invoiceCount: number;
  category: string;
  riskLevel: 'HIGH' | 'MEDIUM' | 'LOW';
}

export default function Vendors() {
  const [vendors, setVendors] = useState<VendorData[]>([]);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const invoices = await apiFetch<Invoice[]>('/api/invoices');

        const vendorMap = new Map<string, VendorData>();

        invoices.forEach((invoice) => {
          const vendorName = invoice.vendor_name || 'Unknown';
          
          if (!vendorMap.has(vendorName)) {
            vendorMap.set(vendorName, {
              name: vendorName,
              totalSpend: 0,
              invoiceCount: 0,
              category: invoice.category || 'Other',
              riskLevel: 'LOW',
            });
          }

          const vendor = vendorMap.get(vendorName)!;
          vendor.totalSpend += invoice.amount || 0;
          vendor.invoiceCount += 1;

          if (vendor.totalSpend > 10000) {
            vendor.riskLevel = 'HIGH';
          } else if (vendor.totalSpend > 5000) {
            vendor.riskLevel = 'MEDIUM';
          }
        });

        setVendors(Array.from(vendorMap.values()).sort((a, b) => b.totalSpend - a.totalSpend));
      } catch (error) {
        console.error('Failed to fetch vendors:', error);
      }
    };

    fetchVendors();
  }, []);

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Vendor Management</h1>
          <p className="text-slate-400">Monitor vendor spend and risk levels</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
            <p className="text-sm text-slate-400 mb-1">Total Vendors</p>
            <p className="text-3xl font-bold text-white">{vendors.length}</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
            <p className="text-sm text-slate-400 mb-1">Total Monthly Spend</p>
            <p className="text-3xl font-bold text-white">
              ${vendors.reduce((sum, v) => sum + v.totalSpend, 0).toFixed(2)}
            </p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
            <p className="text-sm text-slate-400 mb-1">High Risk Vendors</p>
            <p className="text-3xl font-bold text-orange-400">
              {vendors.filter((v) => v.riskLevel === 'HIGH').length}
            </p>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-900/50 border-b border-slate-700/50">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Vendor</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Category</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Invoices</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Total Spend</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Risk Level</th>
              </tr>
            </thead>
            <tbody>
              {vendors.length > 0 ? (
                vendors.map((vendor, index) => (
                  <tr key={index} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-700/50 rounded-lg flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-slate-400" />
                        </div>
                        <span className="text-sm font-medium text-white">{vendor.name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-slate-300">{vendor.category}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-slate-300">{vendor.invoiceCount}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-sm text-white">
                        <DollarSign className="w-4 h-4 text-slate-400" />
                        {vendor.totalSpend.toFixed(2)}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <AlertTriangle
                          className={`w-4 h-4 ${
                            vendor.riskLevel === 'HIGH' ? 'text-red-400' :
                            vendor.riskLevel === 'MEDIUM' ? 'text-orange-400' :
                            'text-green-400'
                          }`}
                        />
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            vendor.riskLevel === 'HIGH' ? 'bg-red-500/20 text-red-400' :
                            vendor.riskLevel === 'MEDIUM' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-green-500/20 text-green-400'
                          }`}
                        >
                          {vendor.riskLevel}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="p-12 text-center text-slate-500">
                    No vendor data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppLayout>
  );
}
